<template>
    <div class="audit-page">
        <div class="container py-4">
            <h1>Agreement Audit</h1>
            <p class="text-muted">Hash: {{ hash }}</p>

            <div v-if="loading" class="text-center py-5">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p class="mt-3">Loading audit data...</p>
            </div>

            <div v-else-if="error" class="alert alert-danger">
                {{ error }}
            </div>

            <div v-else>
                <!-- Plan Info -->
                <div class="card mb-4">
                    <div class="card-header bg-primary text-white">
                        <h5 class="mb-0">Plan Information</h5>
                    </div>
                    <div class="card-body">
                        <pre>{{ JSON.stringify(data.plan, null, 2) }}</pre>
                    </div>
                </div>

                <!-- User Info -->
                <div class="card mb-4">
                    <div class="card-header bg-info text-white">
                        <h5 class="mb-0">User Information</h5>
                    </div>
                    <div class="card-body">
                        <pre>{{ JSON.stringify(data.user, null, 2) }}</pre>
                    </div>
                </div>

                <!-- Signature -->
                <div class="card mb-4" v-if="data.signature">
                    <div class="card-header bg-success text-white">
                        <h5 class="mb-0">Signature</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <h6>Signature Image</h6>
                                <div class="signature-preview" v-if="data.signature.signature_image_base64">
                                    <img :src="data.signature.signature_image_base64" alt="Signature" class="img-fluid border" />
                                </div>
                                <p v-else class="text-muted">No signature image</p>

                                <!-- Stroke Data Replay -->
                                <div v-if="data.signature.stroke_data_json && data.signature.stroke_data_json.length" class="mt-3">
                                    <h6>Signature Replay</h6>
                                    <div class="stroke-replay-container">
                                        <canvas ref="replayCanvas" width="400" height="150" class="replay-canvas"></canvas>
                                        <div class="replay-controls mt-2">
                                            <button class="btn btn-sm btn-outline-primary mr-2" @click="replaySignature" :disabled="isReplaying">
                                                <i class="fas" :class="isReplaying ? 'fa-spinner fa-spin' : 'fa-play'"></i>
                                                {{ isReplaying ? 'Replaying...' : 'Replay' }}
                                            </button>
                                            <button class="btn btn-sm btn-outline-secondary" @click="clearReplay" :disabled="isReplaying">
                                                <i class="fas fa-undo"></i> Reset
                                            </button>
                                            <span class="ml-3 text-muted small" v-if="data.signature.stroke_data_json">
                                                {{ data.signature.stroke_data_json.length }} stroke{{ data.signature.stroke_data_json.length !== 1 ? 's' : '' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <h6>Signature Metadata</h6>
                                <pre>{{ JSON.stringify({
                                    id: data.signature.id,
                                    signature_type: data.signature.signature_type,
                                    typed_value: data.signature.typed_value,
                                    document_hash_before: data.signature.document_hash_before,
                                    document_hash_after: data.signature.document_hash_after,
                                    signature_image_hash: data.signature.signature_image_hash,
                                    stroke_data_hash: data.signature.stroke_data_hash,
                                    created_at: data.signature.created_at
                                }, null, 2) }}</pre>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card mb-4" v-else>
                    <div class="card-header bg-secondary text-white">
                        <h5 class="mb-0">Signature</h5>
                    </div>
                    <div class="card-body text-muted">
                        No signature recorded yet
                    </div>
                </div>

                <!-- Signed Document -->
                <div class="card mb-4" v-if="data.signedDocument">
                    <div class="card-header bg-warning">
                        <h5 class="mb-0">Signed Document</h5>
                    </div>
                    <div class="card-body">
                        <div class="mb-3">
                            <a :href="downloadUrl" target="_blank" class="btn btn-primary">
                                <i class="fas fa-download mr-2"></i>Download Signed PDF
                            </a>
                        </div>
                        <table class="table table-sm mt-3">
                            <tr>
                                <td class="font-weight-bold" style="width: 200px;">Original PDF Hash</td>
                                <td><code>{{ data.signedDocument.original_pdf_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Filled PDF Hash</td>
                                <td><code>{{ data.signedDocument.filled_pdf_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Signed PDF Hash</td>
                                <td><code>{{ data.signedDocument.signed_pdf_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Audit Record Hash</td>
                                <td><code>{{ data.signedDocument.audit_record_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Completed At</td>
                                <td>{{ data.signedDocument.created_at }}</td>
                            </tr>
                        </table>
                    </div>
                </div>

                <!-- Trusted Timestamp (RFC 3161) -->
                <div class="card mb-4" v-if="data.signedDocument && data.signedDocument.tsa_hash_submitted">
                    <div class="card-header" style="background-color: #464577; color: white;">
                        <h5 class="mb-0"><i class="fas fa-shield-alt mr-2"></i>Trusted Timestamp (RFC 3161)</h5>
                    </div>
                    <div class="card-body">
                        <div class="alert alert-success mb-3">
                            <i class="fas fa-check-circle mr-2"></i>
                            This document's signing data has been independently timestamped by <strong>DigiCert</strong> Trusted Timestamp Authority.
                        </div>

                        <table class="table table-sm">
                            <tr>
                                <td class="font-weight-bold" style="width: 200px;">TSA Provider</td>
                                <td>DigiCert Timestamp Authority (<code>http://timestamp.digicert.com</code>)</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Hash Algorithm</td>
                                <td>SHA-256</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Hash Submitted</td>
                                <td><code>{{ data.signedDocument.tsa_hash_submitted }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Timestamp Requested</td>
                                <td>{{ data.signedDocument.tsa_requested_at }}</td>
                            </tr>
                            <tr v-if="data.signedDocument.tsa_response">
                                <td class="font-weight-bold">TSA Token Size</td>
                                <td>{{ data.signedDocument.tsa_response.tsa_response_size }} bytes</td>
                            </tr>
                        </table>

                        <h6 class="mt-4 mb-3" style="color: #464577;">Component Hashes</h6>
                        <p class="small text-muted">The submitted hash is a SHA-256 hash of the following three hashes, sorted alphabetically and joined with colons:</p>
                        <table class="table table-sm" v-if="data.signedDocument.tsa_response && data.signedDocument.tsa_response.component_hashes">
                            <tr>
                                <td class="font-weight-bold" style="width: 200px;">Signed PDF Hash</td>
                                <td><code>{{ data.signedDocument.tsa_response.component_hashes.signed_pdf_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Signature Image Hash</td>
                                <td><code>{{ data.signedDocument.tsa_response.component_hashes.signature_image_hash }}</code></td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Audit Record Hash</td>
                                <td><code>{{ data.signedDocument.tsa_response.component_hashes.audit_record_hash }}</code></td>
                            </tr>
                        </table>

                        <h6 class="mt-4 mb-3" style="color: #464577;">How to Verify This Timestamp</h6>
                        <div class="verification-instructions">
                            <p>This timestamp can be independently verified to prove that the signing data existed at the recorded time. The verification confirms that the hashes were submitted to DigiCert's TSA and have not been altered.</p>

                            <h6 class="mt-3">Step 1: Recreate the Combined Hash</h6>
                            <p>Sort the three component hashes alphabetically, join them with colons, then compute a SHA-256 hash of the result:</p>
                            <pre class="bg-light p-3 rounded">{{ tsaHashInput }}</pre>
                            <pre class="bg-light p-3 rounded">echo -n "{{ tsaHashInput }}" | openssl dgst -sha256
# Expected: {{ data.signedDocument.tsa_hash_submitted }}</pre>

                            <h6 class="mt-3">Step 2: Save the TSA Token</h6>
                            <p>Copy the base64-encoded TSA token below and save it as <code>timestamp.tsr</code>:</p>
                            <div class="position-relative">
                                <pre class="bg-light p-3 rounded tsa-token" style="max-height: 120px; overflow: auto; font-size: 11px;">{{ data.signedDocument.tsa_token }}</pre>
                                <button class="btn btn-sm btn-outline-secondary position-absolute" style="top: 5px; right: 15px;" @click="copyTsaToken">
                                    <i class="fas fa-copy"></i>
                                </button>
                            </div>
                            <pre class="bg-light p-3 rounded mt-2"># Decode the base64 token to a binary file:
echo "{{ truncateToken }}" | base64 -d > timestamp.tsr</pre>

                            <h6 class="mt-3">Step 3: Extract and Verify with OpenSSL</h6>
                            <p>Create a file containing the hash that was submitted, then verify the TSA response:</p>
                            <pre class="bg-light p-3 rounded"># Create the data file with the hash bytes:
echo -n "{{ tsaHashInput }}" | openssl dgst -sha256 -binary > hash.bin

# Verify the timestamp against DigiCert's CA certificate:
# First, download DigiCert's TSA certificate chain:
curl -o digicert_tsa.pem https://cacerts.digicert.com/DigiCertTrustedG4RSA4096SHA256TimeStampingCA.crt.pem

# Verify the timestamp response:
openssl ts -verify -data hash.bin -in timestamp.tsr -CAfile digicert_tsa.pem

# To inspect the timestamp token contents:
openssl ts -reply -in timestamp.tsr -text</pre>

                            <h6 class="mt-3">Step 4: Verify Document Integrity</h6>
                            <p>To verify the signed PDF has not been modified, compute its SHA-256 hash and compare it to the stored hash:</p>
                            <pre class="bg-light p-3 rounded"># Hash the signed PDF:
openssl dgst -sha256 signed_agreement_R{{ data.plan.id }}.pdf

# Compare with stored hash:
# Expected: {{ data.signedDocument.signed_pdf_hash }}</pre>

                            <div class="alert alert-info mt-3">
                                <strong>What does this prove?</strong>
                                <ul class="mb-0 mt-2">
                                    <li>The signed PDF, signature image, and audit record existed at the timestamp recorded by DigiCert</li>
                                    <li>None of these artifacts have been modified since the timestamp was issued</li>
                                    <li>DigiCert's cryptographic signature on the timestamp token is independently verifiable using their public certificate</li>
                                    <li>This provides third-party, non-repudiable proof of when the agreement was signed</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Audit Log -->
                <div class="card mb-4">
                    <div class="card-header bg-dark text-white">
                        <h5 class="mb-0">Audit Log ({{ data.auditLog.length }} events)</h5>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="data.auditLog.length === 0" class="p-3 text-muted">
                            No audit events recorded
                        </div>
                        <table v-else class="table table-sm table-striped mb-0">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Event Type</th>
                                    <th>Event Data</th>
                                    <th>IP Address</th>
                                    <th>Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="entry in data.auditLog" :key="entry.id">
                                    <td>{{ entry.id }}</td>
                                    <td><code>{{ entry.event_type }}</code></td>
                                    <td>
                                        <small><pre class="mb-0" style="max-width: 400px; overflow: auto;">{{ JSON.stringify(entry.event_data_json, null, 2) }}</pre></small>
                                    </td>
                                    <td><small>{{ entry.ip_address }}</small></td>
                                    <td><small>{{ entry.created_at }}</small></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Raw JSON Dump -->
                <div class="card mb-4">
                    <div class="card-header" style="background: #333; color: #0f0;">
                        <h5 class="mb-0">Raw JSON Dump</h5>
                    </div>
                    <div class="card-body" style="background: #1e1e1e;">
                        <pre style="color: #0f0; max-height: 500px; overflow: auto;">{{ JSON.stringify(data, null, 2) }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { apiUrl } from '@/constants/config';

export default {
    name: 'AgreementAudit',
    props: {
        hash: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: true,
            error: null,
            data: null,
            isReplaying: false,
            replayAnimationId: null
        };
    },
    computed: {
        downloadUrl() {
            return `${apiUrl}api/account/agreement-sign/${this.hash}/download-signed`;
        },
        tsaHashInput() {
            return this.data?.signedDocument?.tsa_hash_input || '';
        },
        truncateToken() {
            const token = this.data?.signedDocument?.tsa_token || '';
            if (token.length > 60) return token.substring(0, 60) + '...';
            return token;
        }
    },
    mounted() {
        this.loadAuditData();
    },
    beforeDestroy() {
        if (this.replayAnimationId) {
            cancelAnimationFrame(this.replayAnimationId);
        }
    },
    methods: {
        async loadAuditData() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(`${apiUrl}api/account/agreement-sign/${this.hash}/audit`);
                this.data = response.data;
            } catch (err) {
                console.error('Failed to load audit data:', err);
                this.error = err.response?.data?.error || err.message || 'Failed to load audit data';
            } finally {
                this.loading = false;
            }
        },

        copyTsaToken() {
            const token = this.data?.signedDocument?.tsa_token || '';
            navigator.clipboard.writeText(token).then(() => {
                alert('TSA token copied to clipboard');
            }).catch(() => {
                // Fallback
                const el = document.createElement('textarea');
                el.value = token;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                alert('TSA token copied to clipboard');
            });
        },

        clearReplay() {
            const canvas = this.$refs.replayCanvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        },

        replaySignature() {
            const canvas = this.$refs.replayCanvas;
            if (!canvas || !this.data?.signature?.stroke_data_json) return;

            const strokes = this.data.signature.stroke_data_json;
            if (!strokes.length) return;

            this.isReplaying = true;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Calculate bounding box of all points to scale into canvas
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            strokes.forEach(stroke => {
                (stroke.points || []).forEach(pt => {
                    if (pt.x < minX) minX = pt.x;
                    if (pt.y < minY) minY = pt.y;
                    if (pt.x > maxX) maxX = pt.x;
                    if (pt.y > maxY) maxY = pt.y;
                });
            });

            const dataWidth = maxX - minX || 1;
            const dataHeight = maxY - minY || 1;
            const padding = 15;
            const scaleX = (canvas.width - padding * 2) / dataWidth;
            const scaleY = (canvas.height - padding * 2) / dataHeight;
            const scale = Math.min(scaleX, scaleY);
            const offsetX = padding + ((canvas.width - padding * 2) - dataWidth * scale) / 2 - minX * scale;
            const offsetY = padding + ((canvas.height - padding * 2) - dataHeight * scale) / 2 - minY * scale;

            // Flatten all points with stroke breaks for animation
            const allSegments = [];
            strokes.forEach(stroke => {
                const points = stroke.points || [];
                for (let i = 1; i < points.length; i++) {
                    allSegments.push({
                        x1: points[i - 1].x * scale + offsetX,
                        y1: points[i - 1].y * scale + offsetY,
                        x2: points[i].x * scale + offsetX,
                        y2: points[i].y * scale + offsetY,
                        isStart: i === 1
                    });
                }
            });

            if (!allSegments.length) {
                this.isReplaying = false;
                return;
            }

            ctx.strokeStyle = '#000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            let segIndex = 0;
            // Draw multiple segments per frame for speed
            const segmentsPerFrame = Math.max(1, Math.ceil(allSegments.length / 120));

            const drawNext = () => {
                for (let i = 0; i < segmentsPerFrame && segIndex < allSegments.length; i++, segIndex++) {
                    const seg = allSegments[segIndex];
                    if (seg.isStart) {
                        ctx.beginPath();
                        ctx.moveTo(seg.x1, seg.y1);
                    } else {
                        ctx.beginPath();
                        ctx.moveTo(seg.x1, seg.y1);
                    }
                    ctx.lineTo(seg.x2, seg.y2);
                    ctx.stroke();
                }

                if (segIndex < allSegments.length) {
                    this.replayAnimationId = requestAnimationFrame(drawNext);
                } else {
                    this.isReplaying = false;
                }
            };

            this.replayAnimationId = requestAnimationFrame(drawNext);
        }
    }
};
</script>

<style lang="scss" scoped>
.audit-page {
    background: #f5f5f5;
    min-height: 100vh;

    h1 {
        color: #464577;
    }

    pre {
        font-size: 12px;
        white-space: pre-wrap;
        word-break: break-all;
        margin: 0;
    }

    .signature-preview {
        background: #fff;
        padding: 10px;
        border-radius: 4px;

        img {
            max-height: 150px;
        }
    }

    .stroke-replay-container {
        .replay-canvas {
            border: 1px solid #dee2e6;
            border-radius: 4px;
            background: #fff;
            display: block;
            width: 100%;
            max-width: 400px;
        }
    }

    .table {
        font-size: 13px;

        th {
            background: #f8f9fa;
            position: sticky;
            top: 0;
        }
    }

    .verification-instructions {
        font-size: 13px;

        pre {
            font-size: 12px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
        }

        h6 {
            color: #333;
            font-weight: 600;
        }
    }
}
</style>
